import { useEffect, useState } from 'react';
import {
  collection, onSnapshot, addDoc, deleteDoc,
  doc, serverTimestamp, updateDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link } from 'react-router-dom';

// ── Cloudinary config ────────────────────────────────────────────────────────
const CLOUDINARY_CLOUD_NAME = 'dw2tlnubn';
const CLOUDINARY_UPLOAD_PRESET = 'ff_arena_thumbnails'; // unsigned preset

const sidebarLinks = [
  { to: '/', label: '🏠 Dashboard' },
  { to: '/matches', label: '🎯 Matches' },
  { to: '/results', label: '🏆 Results' },
  { to: '/users', label: '👥 Users' },
  { to: '/deposits', label: '💰 Deposits' },
  { to: '/withdrawals', label: '⬆️ Withdrawals' },
  { to: '/settings', label: '⚙️ Settings' },
  { to: '/password-resets', label: '🔑 Password Resets' },
  { to: '/videos', label: '🎬 Videos', active: true },
];

export default function Videos() {
  const [videos, setVideos]             = useState([]);
  const [url, setUrl]                   = useState('');
  const [title, setTitle]               = useState('');
  const [description, setDesc]          = useState('');
  const [thumbnail, setThumb]           = useState('');
  const [adding, setAdding]             = useState(false);
  const [deletingId, setDelId]          = useState(null);
  const [uploading, setUploading]       = useState(false);
  const [uploadProgress, setProgress]   = useState(0);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'videos'), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setVideos(data);
    });
    return () => unsub();
  }, []);

  // ── Cloudinary upload ───────────────────────────────────────────────────────
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('শুধু image file upload করো!');
      return;
    }

    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', 'ff_arena_thumbnails');

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setProgress(progress);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          setThumb(response.secure_url);
          setUploading(false);
          setProgress(0);
        } else {
          alert('Upload failed! Cloudinary preset check করো।');
          setUploading(false);
        }
      };

      xhr.onerror = () => {
        alert('Upload error!');
        setUploading(false);
      };

      xhr.send(formData);
    } catch (e) {
      alert('Error: ' + e.message);
      setUploading(false);
    }
  };

  const handleAdd = async () => {
    if (!url.trim()) { alert('YouTube URL দাও!'); return; }
    if (!title.trim()) { alert('Title দাও!'); return; }
    setAdding(true);
    try {
      await addDoc(collection(db, 'videos'), {
        url:         url.trim(),
        title:       title.trim(),
        description: description.trim(),
        thumbnail:   thumbnail.trim(),
        active:      true,
        createdAt:   serverTimestamp(),
      });
      setUrl('');
      setTitle('');
      setDesc('');
      setThumb('');
      alert('✅ Video add হয়েছে! App এ দেখা যাবে।');
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setAdding(false);
  };

  const handleToggle = async (video) => {
    await updateDoc(doc(db, 'videos', video.id), { active: !video.active });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('এই video delete করবে?')) return;
    setDelId(id);
    try {
      await deleteDoc(doc(db, 'videos', id));
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setDelId(null);
  };

  // YouTube auto thumbnail fallback
  const getAutoThumbnail = (url) => {
    if (!url) return null;
    const patterns = [
      /youtube\.com\/watch\?v=([^&\n?#]+)/,
      /youtu\.be\/([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
    }
    return null;
  };

  return (
    <div style={styles.container}>

      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}>🎮 FF Arena</div>
        <div style={styles.sidebarSub}>Admin Panel</div>
        <nav style={styles.nav}>
          {sidebarLinks.map(l => (
            <Link key={l.to} to={l.to}
              style={l.active ? styles.navItemActive : styles.navItem}>
              {l.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main */}
      <div style={styles.main}>
        <h1 style={styles.pageTitle}>🎬 Videos</h1>
        <p style={styles.pageSub}>Home Screen এ দেখানোর জন্য YouTube video manage করো</p>

        {/* Add Form */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>➕ নতুন Video Add করো</div>

          <div style={styles.formGroup}>
            <label style={styles.label}>YouTube URL *</label>
            <input
              style={styles.input}
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=XXXXXXXXXXX"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Title *</label>
            <input
              style={styles.input}
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Video এর title"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Description (optional)</label>
            <input
              style={styles.input}
              value={description}
              onChange={e => setDesc(e.target.value)}
              placeholder="সংক্ষিপ্ত বিবরণ"
            />
          </div>

          {/* Thumbnail Upload */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Thumbnail Image (optional)</label>

            {/* Upload button */}
            <label style={{
              ...styles.uploadBtn,
              opacity: uploading ? 0.7 : 1,
              cursor: uploading ? 'not-allowed' : 'pointer',
            }}>
              {uploading
                ? `⏳ Uploading... ${uploadProgress}%`
                : '📁 Device থেকে Image Upload করো'}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </label>

            {/* Progress bar */}
            {uploading && (
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${uploadProgress}%` }} />
              </div>
            )}

            {/* OR divider */}
            <div style={styles.orDivider}>— অথবা URL manually দাও —</div>

            <input
              style={styles.input}
              value={thumbnail}
              onChange={e => setThumb(e.target.value)}
              placeholder="https://example.com/thumbnail.jpg"
              disabled={uploading}
            />
            <div style={styles.thumbHint}>
              ℹ️ না দিলে YouTube এর auto thumbnail দেখাবে
            </div>
          </div>

          {/* Thumbnail Preview */}
          {thumbnail && (
            <div style={styles.previewBox}>
              <div style={styles.previewLabel}>✅ Thumbnail Preview:</div>
              <img
                src={thumbnail}
                alt="thumbnail preview"
                style={styles.previewImg}
                onError={e => e.target.style.display = 'none'}
              />
            </div>
          )}

          <button
            style={{ ...styles.addBtn, opacity: adding || uploading ? 0.6 : 1 }}
            onClick={handleAdd}
            disabled={adding || uploading}
          >
            {adding ? '⏳ Adding...' : '✅ Video Add করো'}
          </button>
        </div>

        {/* Stats */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={{ color: '#1e7fff', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              {videos.length}
            </div>
            <div style={styles.statLabel}>Total Videos</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ color: '#00c896', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              {videos.filter(v => v.active).length}
            </div>
            <div style={styles.statLabel}>Active (App এ দেখাচ্ছে)</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ color: '#888', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              {videos.filter(v => !v.active).length}
            </div>
            <div style={styles.statLabel}>Hidden</div>
          </div>
        </div>

        {/* Info */}
        <div style={styles.infoCard}>
          <span style={{ fontSize: 16 }}>ℹ️</span>
          <span style={{ color: '#888', fontSize: 13 }}>
            App এ সর্বশেষ <b style={{ color: '#f4c430' }}>3টি active video</b> দেখাবে।
            Toggle করে যেকোনো video hide/show করা যাবে।
          </span>
        </div>

        {/* Video List */}
        <div style={styles.videoGrid}>
          {videos.length === 0 && (
            <div style={{ color: '#555', textAlign: 'center', padding: 40, gridColumn: '1/-1' }}>
              কোনো video নেই। উপরে add করো।
            </div>
          )}
          {videos.map(v => {
            const thumb = v.thumbnail || getAutoThumbnail(v.url);
            return (
              <div key={v.id} style={{
                ...styles.videoCard,
                opacity: v.active ? 1 : 0.5,
                borderColor: v.active ? '#1a4a7a' : '#1e1e3a',
              }}>
                {thumb && (
                  <img
                    src={thumb}
                    alt={v.title}
                    style={styles.thumb}
                    onError={e => e.target.style.display = 'none'}
                  />
                )}
                <div style={styles.videoInfo}>
                  <div style={styles.videoTitle}>{v.title}</div>
                  {v.description && (
                    <div style={styles.videoDesc}>{v.description}</div>
                  )}
                  <div style={styles.videoUrl}>{v.url}</div>
                  {v.thumbnail && (
                    <div style={{ color: '#00c896', fontSize: 11, marginBottom: 4 }}>
                      ✅ Custom thumbnail
                    </div>
                  )}
                  <div style={styles.videoTime}>
                    {v.createdAt?.seconds
                      ? new Date(v.createdAt.seconds * 1000).toLocaleString()
                      : '—'}
                  </div>
                </div>
                <div style={styles.videoActions}>
                  <button
                    style={{
                      ...styles.toggleBtn,
                      background: v.active ? '#00c89622' : '#88888822',
                      border: `1px solid ${v.active ? '#00c89644' : '#88888844'}`,
                      color: v.active ? '#00c896' : '#888',
                    }}
                    onClick={() => handleToggle(v)}
                  >
                    {v.active ? '✅ Active' : '⏸ Hidden'}
                  </button>
                  <button
                    style={{ ...styles.deleteBtn, opacity: deletingId === v.id ? 0.6 : 1 }}
                    onClick={() => handleDelete(v.id)}
                    disabled={deletingId === v.id}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', minHeight: '100vh', background: '#0a0a1a' },
  sidebar: {
    width: 240, background: '#111128', borderRight: '1px solid #1e1e3a',
    padding: '24px 0', display: 'flex', flexDirection: 'column',
    position: 'fixed', height: '100vh',
  },
  sidebarLogo: { color: '#f4c430', fontSize: 20, fontWeight: 'bold', padding: '0 24px', marginBottom: 4 },
  sidebarSub:  { color: '#555', fontSize: 12, padding: '0 24px', marginBottom: 32 },
  nav:         { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: {
    color: '#888', textDecoration: 'none', padding: '12px 24px',
    fontSize: 14, fontWeight: '500', borderLeft: '3px solid transparent',
  },
  navItemActive: {
    color: '#f4c430', textDecoration: 'none', padding: '12px 24px',
    fontSize: 14, fontWeight: '600', borderLeft: '3px solid #f4c430',
    background: '#f4c43011',
  },
  main:      { marginLeft: 240, flex: 1, padding: 32 },
  pageTitle: { color: '#fff', fontSize: 28, fontWeight: 'bold', margin: 0 },
  pageSub:   { color: '#666', fontSize: 14, marginTop: 6, marginBottom: 24 },

  card: {
    background: '#111128', borderRadius: 16,
    border: '1px solid #1e1e3a', padding: 24, marginBottom: 24,
  },
  cardTitle: { color: '#fff', fontSize: 16, fontWeight: 'bold', marginBottom: 20 },
  formGroup: { marginBottom: 16 },
  label: { color: '#888', fontSize: 13, fontWeight: '600', display: 'block', marginBottom: 8 },
  input: {
    width: '100%', background: '#0a0a1a', border: '1px solid #333',
    borderRadius: 10, color: '#fff', fontSize: 14,
    padding: '12px 14px', outline: 'none', boxSizing: 'border-box',
  },
  uploadBtn: {
    display: 'block', width: '100%',
    background: '#1e7fff22', border: '1px dashed #1e7fff66',
    borderRadius: 10, color: '#1e7fff',
    fontWeight: '600', fontSize: 14,
    padding: '14px', textAlign: 'center',
    marginBottom: 8, boxSizing: 'border-box',
  },
  progressBar: {
    height: 6, backgroundColor: '#1e1e3a',
    borderRadius: 4, marginBottom: 10, overflow: 'hidden',
  },
  progressFill: {
    height: '100%', backgroundColor: '#1e7fff',
    borderRadius: 4, transition: 'width 0.3s ease',
  },
  orDivider: {
    color: '#555', fontSize: 12,
    textAlign: 'center', margin: '10px 0',
  },
  thumbHint:   { color: '#555', fontSize: 11, marginTop: 6 },
  previewBox:  { marginBottom: 16 },
  previewLabel:{ color: '#00c896', fontSize: 12, marginBottom: 8 },
  previewImg:  {
    width: 240, height: 135, objectFit: 'cover',
    borderRadius: 8, border: '1px solid #333',
  },
  addBtn: {
    background: '#1e7fff', border: 'none', borderRadius: 12,
    color: '#fff', fontWeight: 'bold', fontSize: 15,
    padding: '14px 32px', cursor: 'pointer',
  },

  statsRow: { display: 'flex', gap: 16, marginBottom: 20 },
  statCard: {
    background: '#111128', borderRadius: 14, border: '1px solid #1e1e3a',
    padding: '20px 28px', minWidth: 160,
  },
  statLabel: { color: '#666', fontSize: 13 },

  infoCard: {
    background: '#1e7fff11', border: '1px solid #1e7fff33',
    borderRadius: 10, padding: '12px 16px',
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24,
  },

  videoGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 16,
  },
  videoCard: {
    background: '#111128', borderRadius: 16,
    border: '1px solid', overflow: 'hidden',
  },
  thumb:      { width: '100%', height: 180, objectFit: 'cover' },
  videoInfo:  { padding: '14px 16px' },
  videoTitle: { color: '#fff', fontSize: 15, fontWeight: '700', marginBottom: 4 },
  videoDesc:  { color: '#888', fontSize: 13, marginBottom: 6 },
  videoUrl:   { color: '#1e7fff', fontSize: 11, marginBottom: 4, wordBreak: 'break-all' },
  videoTime:  { color: '#555', fontSize: 11 },
  videoActions: { padding: '0 16px 16px', display: 'flex', gap: 10 },
  toggleBtn: {
    borderRadius: 8, padding: '7px 14px',
    cursor: 'pointer', fontSize: 12, fontWeight: '600', flex: 1,
  },
  deleteBtn: {
    background: '#ff444422', border: '1px solid #ff444444',
    color: '#ff6b6b', borderRadius: 8, padding: '7px 14px',
    cursor: 'pointer', fontSize: 12, fontWeight: '600',
  },
};